import { styled } from '@/styles/stitches.config';
import type { ComposableStyledComponentProps } from '@/types/composable-styled-component';
import { countryIdToName } from '@/utils/country-id-to-name';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import React from 'react';
import { Label } from '../label';
import { Tooltip } from '../tooltip';
import { useTooltipContext } from './tooltipContext';
import { CountryData } from '@/pages/about';

const TooltipDataList = styled('dl', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$3',
  margin: 0,
});

const TooltipDataItemRoot = styled('div', {
  gridRow: 'auto',
  gridColumn: 'auto',
  variants: {
    firstItem: {
      true: {
        gridRow: '1',
        gridColumn: 'span 2',
      },
    },
  },
});

const Term = styled('dt', {
  lineHeight: '$1',
});

const Definition = styled('dd', {
  lineHeight: '$1',
  marginInlineStart: 0,
});

const TooltipHeader = styled('div', {
  display: 'flex',
  gap: '1em',
});

const H3 = styled('h3', {
  color: '$unicef',
  marginBlockEnd: '$3',
  flex: 1,
});

const CloseButton = styled('button', {
  margin: 0,
  padding: 0,
  width: '25px',
  height: '25px',
  backgroundColor: '$white',
  color: '$blue1',
  border: 'none',

  '&:hover': {
    color: '$blue0',
  },
});

type TooltipDataItemProps = ComposableStyledComponentProps & {
  label: string;
  value: number;
  isFirstItem: boolean;
};

function TooltipDataItem(props: TooltipDataItemProps) {
  const { css, label, value, isFirstItem } = props;
  const formatNumber = useNumberFormatting('decimal');

  return (
    <TooltipDataItemRoot css={css} firstItem={isFirstItem}>
      <Term>
        <Label>{label}</Label>
      </Term>
      <Definition>
        <Label css={{ color: '$unicef' }}>{formatNumber(value)}</Label>
      </Definition>
    </TooltipDataItemRoot>
  );
}

export type AboutPageMapTooltipProps = {
  data: CountryData;
  getItems: (d: CountryData) => [string, number | undefined][];
  root: Element;
};

export function AboutPageMapTooltip({ root, data, getItems }: AboutPageMapTooltipProps) {
  const { getId, closeTooltip } = useTooltipContext();

  return (
    <Tooltip
      root={root}
      options={{
        placement: 'right-start',
        modifiers: [
          { name: 'offset', enabled: true, options: { offset: [24, 0] } },
          { name: 'preventOverflow', options: { mainAxis: true, altAxis: true, padding: 16 } },
        ],
      }}
    >
      <TooltipHeader>
        <H3>{countryIdToName(getId(data))}</H3>
        <CloseButton onClick={closeTooltip}>
          <svg
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="12.5" cy="12.5" r="12" stroke="currentColor" />
            <path d="M17 8L8 17" stroke="currentColor" />
            <path d="M8 8L17 17" stroke="currentColor" />
          </svg>
        </CloseButton>
      </TooltipHeader>

      <TooltipDataList>
        {getItems(data)
          .filter(([_, value]) => value !== undefined)
          .map(([label, value], i) => (
            <TooltipDataItem key={label} isFirstItem={i === 0} label={label} value={value || 0} />
          ))}
      </TooltipDataList>
    </Tooltip>
  );
}
